import React from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { withFormik } from 'formik';
import * as yup from "yup";
import moment from 'moment';

import BookingForm from './BookingForm';
import styles from './addnewbooking.module.css';


function AddNewBooking(props) {

    const user = useSelector(({ generalSetting }) => generalSetting && generalSetting.setting);
    const guestList = useSelector(({ guests }) => guests && guests.guestList);
    const rentalList = useSelector(({ rentals }) => rentals && rentals.rentals);
    const generalSetting = useSelector(({ generalSetting }) => generalSetting && generalSetting.setting);

    const phoneRegExp = /^[0-9-+\s()]*$/;

    const findRentalDetail = (rentalId) => {
        const currency = generalSetting.currency.value;
        const rentalDetail = rentalList.find((rental) => Number(rental.id) === Number(rentalId));
        return rentalDetail || {
            id: '',
            maxOccup: 1,
            maxChild: 0,
            checkInTimeAt: 'from',
            checkOutTimeAt: 'until',
            checkinTime: '15:00',
            checkoutTime: '10:00',
            currency: currency,
        };
    };

    const getContactPayload = (data) => {
        const contactPayload = {
            name: data.name,
            emailId: data.emailId,
            secondaryEmailId: data.secondaryEmailId ?? "",
            phoneNo: data?.phoneNo.split(" ")?.length !== 1 ? data.phoneNo : "",
            secondaryPhoneNo: data?.secondaryPhoneNo.split(" ")?.length !== 1 ? data.secondaryPhoneNo : "",
            postalCode: data.postalCode ?? "",
            address: data.address ?? "",
            street: data.street ?? "",
            state: data.state ?? "",
            country: data.country ?? "",
            nationality: data.nationality ?? "",
            language: data.language ?? "",
            company: data.company ?? "",
            companyAddress: data.companyAddress ?? "",
            notes: data.moreInfo ?? "",
            dob: data.dob ? moment(data.dob).format("YYYY-MM-DD") : "",
        };
        if (data.selectedContact) {
            contactPayload.id = data.id;
        };
        return contactPayload;
    };

    const contactInitialValues = {
        checkboxGuestMadeReservation: true,
        selectedContact: false,
        id: null,
        name: "",
        emailId: "",
        secondaryEmailId: "",
        phoneNo: "",
        secondaryPhoneNo: "",
        postalCode: "",
        country: user.country,
        address: "",
        street: "",
        state: "",
        nationality: "",
        language: "",
        company: "",
        companyAddress: "",
        moreInfo: "",
        dob: "",
    };

    const otherInitialValues = {
        overWriteTotalToggle: false,
        newTotal: 0,
        finalPrice: 0,
        extraGuestFee: 0,
        fees: 0,
        taxes: 0,
        baseRate: 0,
        maxOccup: 1,
        maxChild: 0,
        checkboxAdditionalGuests: false,
    };

    const initialValues = {

        // Booking - initialValues
        bookingType: "booking",
        title: "",
        rentalId: "",
        additionalGuestNames: "",
        totalGuests: 1,
        adult: 1,
        children: 0,
        babies: 0,
        pets: 0,
        netAmount: 0,
        arrive: "",
        depart: "",
        checkInTimeAt: "from",
        checkOutTimeAt: "until",
        checkInTime: "15:10",
        checkOutTime: "10:00",
        nights: 0,
        status: "Booked",
        source: "Beehaz",
        feesAndTaxes: 0,
        discount: 0,
        extraCharge: 0,
        price: 0,
        paymentStatus: "Payment Pending",
        miniNotes: "",
        notes: "",
        color: "#d3d3d3",

        // Guest - initialValues
        guest: contactInitialValues,
        
        // Booker - initialValues
        booker: contactInitialValues,

        // Other initial values
        ...otherInitialValues,

    };

    const contactValidationSchema = {
        name: yup.string().max(150, 'Must be 150 characters or less')
            .test(
                'is-required',
                'Name is a required field', function (value) {
                    return value !== undefined && value !== null && value.trim() !== '';
            }),
        company: yup.string().max(150, 'Must be 150 characters or less'),
        companyAddress: yup.string().max(150, 'Must be 150 characters or less'),
        emailId: yup.string().max(150, 'Must be 150 characters or less').email('Invalid email'),
        secondaryEmailId: yup.string().max(150, 'Must be 150 characters or less').email('Invalid email'),
        phoneNo: yup.string().max(50, 'Must be 50 characters or less').matches(phoneRegExp, 'Only numbers, space, parenthesis, + and - are accepted.').nullable(),
        secondaryPhoneNo: yup.string().max(50, 'Must be 50 characters or less').matches(phoneRegExp, 'Only numbers, space, parenthesis, + and - are accepted.').nullable(),
        country: yup.string().max(50, 'Must be 50 characters or less'),
        address: yup.string().max(150, 'Must be 150 characters or less'),
        postalCode: yup.string().max(50, 'Must be 50 characters or less'),
        street: yup.string().max(150, 'Must be 150 characters or less'),
        state: yup.string().max(150, 'Must be 150 characters or less'),
        nationality: yup.string().max(150, 'Must be 150 characters or less'),
        language: yup.string().max(150, 'Must be 150 characters or less'),
        moreInfo: yup.string().max(250, 'Must be 250 characters or less'),
        dob: yup.date().typeError('Must be a value of type date').nullable(),
    };

    const validationSchema = yup.object().shape({

        // Booking validation schema
        rentalId: yup.number().required('Rental selection is required').integer('Must be an integer').typeError('Rental selection is required'),
        totalGuests: yup.number().required('At least 1 guest is required').min(1, 'Must be a number greater than or equal to 1').integer('Must be an integer').typeError('Must be a value of type number')
            .test('totalGuests', function (value) {
                const maxOccup = this.parent.maxOccup;
                return value <= maxOccup ? true : this.createError({
                    path: this.path,
                    message: `Maximum ${maxOccup} guests are allowed`,
                });
            }),
        adult: yup.number().required('At least 1 adult is required').min(1, 'At least 1 adult is required').max(100, 'Must be a number less than or equal to 100').integer('Must be an integer').typeError('Must be a value of type number'),
        children: yup.number().min(0, 'Must be a positive number').integer('Must be an integer').typeError('Must be a value of type number')
            .test('children', function (value) {
                const maxChild = this.parent.maxChild;
                return value <= maxChild || !value  ? true : this.createError({
                    path: this.path,
                    message: `Maximum ${maxChild} children are allowed`,
                });
            }),
        babies: yup.number().min(0, 'Must be a positive number').max(100, 'Must be a number less than or equal to 100').integer('Must be an integer').typeError('Must be a value of type number'),
        pets: yup.number().min(0, 'Must be a positive number').max(100, 'Must be a number less than or equal to 100').integer('Must be an integer').typeError('Must be a value of type number'),
        netAmount: yup.number().min(0, 'Must be a positive number').typeError('Must be a value of type number'),
        arrive: yup.date().required('Arrival date is required').typeError('Must be a value of type date').max(`${moment().add(2, 'years').format('YYYY-MM-DD')}`, 'Booking not allowed after 2 years'),
        depart: yup.date().required('Departure date is required').typeError('Must be a value of type date').max(`${moment().add(2, 'years').format('YYYY-MM-DD')}`, 'Booking not allowed after 2 years'),
        checkInTime: yup.string().required('Check-In is required'),
        checkOutTime: yup.string().required('Check-Out is required'),
        nights: yup.number().min(0, 'Must be a positive number').integer('Must be an integer').typeError('Must be a value of type number')
            .test('nights', function (value) {
                let message;
                const minStay = this.parent.minStay;
                const maxStay = this.parent.maxStay;
                if (value < minStay) {
                    message = `Minimum stay for this rental is ${minStay} day${minStay > 1 ? 's' : ''}`;
                } else if (value > maxStay && maxStay > 0) {
                    message = `Maximum stay for this rental is ${maxStay} day${maxStay > 1 ? 's' : ''}`;
                };
                return !message ? true : this.createError({
                    path: this.path,
                    message: message,
                });
            }),
        source: yup.string(),
        paymentStatus: yup.string(),
        additionalGuestNames: yup.string().max(150, 'Must be 150 characters or less'),
        miniNotes: yup.string().max(150, 'Must be 150 characters or less'),
        notes: yup.string().max(900, 'Must be 900 characters or less'),
        color: yup.string(),
        feesAndTaxes: yup.number().min(0, 'Must be a positive number').typeError('Must be a value of type number'),
        discount: yup.number().typeError('Must be a value of type number'),
        extraCharge: yup.number().min(0, 'Must be a positive number').typeError('Must be a value of type number'),
        price: yup.number().min(0, 'Must be a positive number').typeError('Must be a value of type number'),
        finalPrice: yup.number().min(0, 'Must be a positive number').typeError('Must be a value of type number'),
        newTotal: yup.number().min(0, 'Must be a positive number').typeError('Must be a value of type number'),

        // Guest validation schema
        guest: yup.object().shape(contactValidationSchema),
        
        // Booker validation schema
        booker: yup.object().shape({
            ...contactValidationSchema,
            name: yup.string().max(150, 'Must be 150 characters or less')
            .test(
                'is-required',
                'Name is a required field', function (value) {
                if (this.parent.checkboxGuestMadeReservation) {
                    return true;
                };
                return value !== undefined && value !== null && value.trim() !== '';
            }),
        }),

    });

    const handleSubmit = (data) => {

        const payloadBooking = {
            bookingType: "booking",
            rentalId: Number(data.rentalId),
            noOfGuests: Number(data.totalGuests),
            noOfAdults: Number(data.adult),
            noOfChildren: Number(data.children),
            noOfBabies: Number(data.babies),
            noOfPets: Number(data.pets),
            netAmount: Number(data.netAmount),
            arrive: moment(data.arrive).utc(true).format("YYYY-MM-DD"),
            depart: moment(data.depart).utc(true).format("YYYY-MM-DD"),
            arriveUTC: moment(data.arrive).utc(true).format("YYYY-MM-DD HH:mm:ss"),
            departUTC: moment(data.depart).utc(true).format("YYYY-MM-DD HH:mm:ss"),
            checkInTimeAt: data.checkInTimeAt,
            checkOutTimeAt: data.checkOutTimeAt,
            checkInTime: data.checkInTime,
            checkOutTime: data.checkOutTime,
            nights: data.nights,
            status: data.status,
            source: data.source,
            fees: Number(Number(data.fees).toFixed(2)),
            tax: Number(Number(data.taxes).toFixed(2)),
            extraGuestFee: Number(Number(data.extraGuestFee).toFixed(2)),
            baseRate: Number(Number(data.baseRate).toFixed(2)),
            discountName: "Discount",
            discountAmount: Number(Number(data.discount).toFixed(2)),
            chargeName: "Extra charge",
            chargeAmount: Number(Number(data.extraCharge).toFixed(2)),
            price: Number(Number(data.overWriteTotalToggle ? data.newTotal : data.finalPrice).toFixed(2)),
            paymentStatus: data.paymentStatus,
            additionalGuestNames: data.additionalGuestNames,
            miniNotes: data.miniNotes,
            notes: data.notes,
            color: data.color,
        };

        const payloadGuest = getContactPayload(data.guest);
        const payloadBooker = getContactPayload(data.booker);

        const payload = {
            payloadBooking: {
                guestMadeReservation: data.booker.checkboxGuestMadeReservation,
                data: payloadBooking,
            },
            payloadGuest: {
                isNew: !data.guest.selectedContact,
                data: payloadGuest,
            },
            payloadBooker: {
                isNew: !data.booker.selectedContact,
                data: payloadBooker,
            },
        };

        props.handleSave(payload);

    };

    const FormikEnhancer = withFormik({
        displayName: 'BookingForm',
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        mapPropsToValues: () => (initialValues),
        handleSubmit: (values, { setSubmitting }) => {
            handleSubmit(values);
            setSubmitting(false);
        },
    })(BookingForm);

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title className={styles.modalTitle}>
                    Add new booking
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormikEnhancer
                    guests={guestList}
                    rentals={rentalList}
                    findRentalDetail={findRentalDetail}
                    newBookingFromEmptyNight={props?.newBookingFromEmptyNight}
                />
            </Modal.Body>
        </>
    );
}

export default AddNewBooking;
